import React, { useState } from 'react';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConfirmDialogVariant = 'danger' | 'warning';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when the user confirms; may be async */
  onConfirm: () => Promise<void> | void;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  const Icon = variant === 'danger' ? AlertCircle : AlertTriangle;
  const iconClass = variant === 'danger' ? 'text-red-500' : 'text-amber-500';

  // The title goes in the modal's own header row — top left, with the X on the
  // right. Drawn in the body instead, the header held only the X, which sat in
  // the left corner over an empty band.
  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      size="sm"
      title={
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 flex-shrink-0 ${iconClass}`} aria-hidden="true" />
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        </div>
      }
      hideCloseButton={loading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">{message}</p>
    </Modal>
  );
}
